/**
 * SaaS domain (plans/pricing/products) - built on apiClient via useRequest,
 * not useAppServices(). Covers the `saas.*` lookup-table block in
 * hook/services.js, consumed across:
 *   - Saas/index.js (agencysaas, potentialproducts)
 *   - Saas/components/data-tables/data/dataTableData.js (getSaas / delete)
 *   - Saas/components/accounts/components/data-tables/data/dataTableData.js
 *     (getProductLocations, create_account_manually)
 *   - Saas/components/CreateModal/MultistepFormWizard.js (createSaas)
 *   - Saas/components/EditsaasModal/MultistepFormWizard.js (editSaas)
 *   - Saas/components/configration/index.js (getStripeProducts,
 *     updateSaasConfigration, fetch_category_pricing, categorypricing)
 *   - Saas/NormalTable.js (deleteSaasConfigration, showonaccountlevel,
 *     updateSaasById)
 *   - Saas/PotentialPricing.js, Saas/Pricing.js (create_accounts)
 *   - Saas/SettingTab.js (subscription_setting, fetch_category_pricing,
 *     categorypricing, edit_category_pricing, delete_category_pricing)
 *
 * Every mutation here invalidates the ["saas"] / ["saas-category-pricing"]
 * prefixes - matches the broad onLoad()/handleRefresh() invalidations the
 * pre-migration call sites already did (invalidateQueries matches by array
 * prefix, so invalidating ["saas"] also refreshes ["saas", agencyId] etc).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST, PUT, DELETE } = ReqMethods;

export const saasKeys = {
  all: ["saas"],
  list: (agencyId) => ["saas", agencyId],
  agencySaas: (agencyId) => ["saas", "agencysaas", agencyId],
  potential: (agencyId) => ["saas", "potential-products", agencyId],
};

// Saas/index.js - the agency's purchased/configured saas products.
export function useAgencySaasProducts(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: saasKeys.agencySaas(agencyId),
    queryFn: async () => {
      const { response, error } = await request(GET, "/saas/agencysaas", {
        query: `agency_id=${agencyId}`,
        toaster: false,
      });
      if (error) throw error;
      return response;
    },
    enabled: !!agencyId,
    ...options,
  });
}

// Saas/index.js - products the agency could still configure/connect.
export function usePotentialSaasProducts(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: saasKeys.potential(agencyId),
    queryFn: async () => {
      const { response, error } = await request(GET, "/saas/potentialproducts", {
        query: `agency_id=${agencyId}`,
        toaster: false,
      });
      if (error) throw error;
      return response;
    },
    enabled: !!agencyId,
    ...options,
  });
}

// Saas/components/data-tables/data/dataTableData.js - the configured plans
// table (products + prices).
export function useSaasPlans(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: saasKeys.list(agencyId),
    queryFn: async () => {
      const { response, error } = await request(GET, "/saas/filter", {
        query: `agency_id=${agencyId}`,
        toaster: false,
      });
      if (error) throw error;
      return response;
    },
    enabled: !!agencyId,
    ...options,
  });
}

// Shared by configration/index.js and CreateModal/MultistepFormWizard.js -
// same key both places already used (["stripe-products", agencyId]), so this
// is one cache entry regardless of which component queries first. Distinct
// from queries/agency.js's useStripeProducts, which hits a different
// endpoint (/agency/getproducts, no agency_id query, no plan context).
export function useSaasStripeProducts(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["stripe-products", agencyId],
    queryFn: async () => {
      const { response, error } = await request(GET, "/saas/get-products", {
        query: `agency_id=${agencyId}`,
      });
      if (error) throw error;
      return response;
    },
    enabled: !!agencyId,
    ...options,
  });
}

// Saas/components/accounts/.../dataTableData.js - accounts created from a
// given saas product.
export function useProductLocations(productId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["saas-product-locations", productId],
    queryFn: async () => {
      const { response, error } = await request(GET, "/saas/getProductLocations", {
        query: `product_id=${productId}`,
        toaster: false,
      });
      if (error) throw error;
      return response;
    },
    enabled: !!productId,
    ...options,
  });
}

// Shared by configration/index.js, CreateModal/MultistepFormWizard.js,
// EditsaasModal/MultistepFormWizard.js and SettingTab.js - the plan-category
// (currency) list, same key all four already used.
export function useCategoryPricing(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["saas-category-pricing", agencyId],
    queryFn: async () => {
      // Backend route is POST despite reading from req.query, not a body.
      const { response, error } = await request(POST, "/saas/fetch_category_pricing", {
        query: `agency_id=${agencyId}`,
      });
      if (error) throw error;
      return response;
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useCreateSaasPlan() {
  const request = useRequest();
  return useMutation({
    mutationFn: (payload) => request(POST, "/saas", { payload, toaster: true }),
  });
}

export function useEditSaasPlan() {
  const request = useRequest();
  return useMutation({
    mutationFn: (payload) => request(POST, "/saas/editSaas", { payload }),
  });
}

export function useUpdateSaasConfiguration() {
  const request = useRequest();
  return useMutation({
    mutationFn: (payload) => request(POST, "/saas/updateSaasConfigration", { payload, toaster: true }),
  });
}

// ponytail: the old call site was `AppService.saas.delete(...)`, which never
// existed on the saas lookup table (only `deleteSaas` does, at this same
// DELETE /saas/delete path) - so this delete button has always thrown a
// client-side TypeError and done nothing. Wired to the real `deleteSaas`
// endpoint since it's the only matching method in the namespace; flagged in
// the migration report since this makes the button actually work for the
// first time instead of silently no-op'ing.
export function useDeleteSaasPlan() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (query) => request(DELETE, "/saas/delete", { query }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: saasKeys.all }),
  });
}

export function useCreateAccountManually() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/saas/create_account_manually", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["saas-product-locations"] }),
  });
}

// PotentialPricing.js / Pricing.js - auto-creates accounts for every
// location under a saas product.
export function useCreateAccounts() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/saas/create_accounts", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: saasKeys.all }),
  });
}

export function useDeleteSaasConfiguration() {
  const request = useRequest();
  return useMutation({
    mutationFn: (payload) => request(POST, "/saas/deleteSaasConfigration", { payload }),
  });
}

export function useToggleSaasAccountLevel() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/saas/showonaccountlevel", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: saasKeys.all }),
  });
}

export function useUpdateSaasById() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/saas/update-plan-by-id", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: saasKeys.all }),
  });
}

export function useCreateCategoryPricing() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/saas/categorypricing", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["saas-category-pricing"] }),
  });
}

export function useEditCategoryPricing() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/saas/edit_category_pricing", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["saas-category-pricing"] }),
  });
}

export function useDeleteCategoryPricing() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/saas/delete_category_pricing", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["saas-category-pricing"] }),
  });
}

// SettingTab.js - persisted onto the Agency document, so the agency cache
// (not any "saas" key) is what needs refreshing - same as the pre-migration
// comment next to this call already noted.
export function useSubscriptionSetting() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/saas/subscription_setting", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agency"] }),
  });
}
